import { Timestamp } from '@angular/fire/firestore';
/** Feature flag: sub-tasks are enabled on dev/test, disabled on prod for now. */
/* export const SUB_ORDERS_ENABLED =
  environment.appName?.includes('.test') || environment.appName?.includes('.dev') || false; */

export const SUB_ORDERS_ENABLED = true

export type WorkOrderState =
  'open' | 'orderAccepted' | 'wip' | 'done' | 'cantDo' | 'wontDo' | 'error' | 'overdue';
export type StepState = 'open' | 'done';
export type WorkOrderPriority = 1 | 0 | -1; // high | medium | low
export type StepType = 'attachment' | 'bool';

export type StepScope = 'dog' | 'user';

export type StepFileContent = 'gpsTrack' | 'classifiedPoints';

export interface WorkOrderStep {
  identifier?: string;
  title: string;
  comment?: string;
  optional?: boolean;
  state: StepState;
  type?: StepType;
  /** Also editable/performable by the WorkOrder's responsibleUserId ("Hauptverantwortlicher"). */
  editableByResponsibleUser?: boolean;
  scope?: StepScope | null;
  fileContent?: StepFileContent | null;
  dogIds?: string[] | null;
  userIds?: string[] | null;
}

export const STEP_TYPES: { value: StepType; label: string }[] = [
  { value: 'bool',       label: 'COCKPIT.WORK_ORDER.check'},//'Abhaken'
  { value: 'attachment', label: 'COCKPIT.WORK_ORDER.file_upload'},//'Datei-Upload' 
];

export const STEP_SCOPES: { value: StepScope; label: string }[] = [
  { value: 'dog',  label: 'COCKPIT.WORK_ORDER.per_dog' },
  { value: 'user', label: 'COCKPIT.WORK_ORDER.per_user' },
];

export const STEP_FILE_CONTENTS: { value: StepFileContent; label: string; disabled?: boolean }[] = [
  { value: 'gpsTrack',         label: 'COCKPIT.WORK_ORDER.gps_track' },
  { value: 'classifiedPoints', label: 'COCKPIT.WORK_ORDER.classified_points' },
];

/** Geo data formats — the only types accepted once a step declares a fileContent. */
export const GEO_FILE_ACCEPT = '.kml,.kmz,.gpx,.geojson,.json';
export const GEO_FILE_EXTENSIONS = ['kml', 'kmz', 'gpx', 'geojson', 'json'];

export function isGeoFileName(name: string): boolean {
  const ext = (name.split('.').pop() ?? '').toLowerCase();
  return GEO_FILE_EXTENSIONS.includes(ext);
}

export function scopeLabel(scope?: StepScope | null): string {
  return STEP_SCOPES?.find(s => s.value === scope)?.label ?? '';
}

export function fileContentLabel(content?: StepFileContent | null): string {
  return STEP_FILE_CONTENTS?.find(c => c.value === content)?.label ?? '';
}

/** How a single point out of a `classifiedPoints` file was classified by the user. */
export type PointClassification = 'cadaverFind' | 'geoObject' | 'sighting';

export const POINT_CLASSIFICATIONS: { value: PointClassification; label: string; icon: string }[] = [
  { value: 'cadaverFind', label: 'COCKPIT.WORK_ORDER.cadaver', icon: 'pest_control' },
  { value: 'geoObject',   label: 'COCKPIT.OBJECTS.PointOfInterest',  icon: 'place' },
  { value: 'sighting',    label: 'COCKPIT.WORK_ORDER.sighting',    icon: 'visibility' },
];

export interface WorkOrderAttachment {
  name: string;
  reference: string;
  url: string;
  /**
   * Dog id or user id the file was uploaded for — present only on scoped step
   * attachments, where it is the folder the file lives in.
   */
  ownerId?: string;
}

export interface WorkOrder {
  identifier?: string;
  contextId: string;

  templateID?: string;
  title?: string;
  type?: string;
  templateNote?: string;

  note?: string;
  locationCoordinates?: { latitude: number; longitude: number };

  dueDate?: Date | Timestamp | null;
  plannedExecutionDate?: Date | Timestamp | null;
  priority: WorkOrderPriority;

  link?: string;
  targetUserIds?: string[];
  // nly shown when the template enables it
  responsibleUserId?: string;
  assignedDogIds?: string[];

  /** Per-context sharing when created in a super-context: { <contextId>: 'update' | 'view' }. */
  contextIdRights?: Record<string, 'view' | 'update'>;
  contextIds?: string[];

  parentWorkOrderId?: string;

  /**
   * Set on a parent order created early — before the user hit Save — just so a
   * sub-task can be added on the fly during creation. Cleared on the real save
   */
  draft?: boolean;

  steps?: WorkOrderStep[];
  state: WorkOrderState;
  advancedStatusTracking?: boolean;

  /** 0–100, written by the search-job processing. */
  coveragePercentage?: number | null;
  workQuality?: string | null;

  // set by the Accept/Start/Finish Order actions.
  orderAcceptedTimestamp?: Date | Timestamp | null;
  orderStartedTimestamp?: Date | Timestamp | null;
  orderFinishedTimestamp?: Date | Timestamp | null;

  createdUserId: string;
  createdUserDisplayName: string;
  createdDeviceName: string;
  creationDate?: Date | Timestamp;

  editorUserId?: string;
  editorUserDisplayName?: string;
  editorDeviceName?: string;

  updateTimestamp?: Date | Timestamp | null;
}

export const WORK_ORDER_STATES: { value: WorkOrderState; label: string }[] = [
  { value: 'open',          label: 'work_order_states_labels.open' },
  { value: 'orderAccepted', label: 'work_order_states_labels.orderaccepted' },
  { value: 'wip',     label: 'work_order_states_labels.wip' },
  { value: 'done',    label: 'work_order_states_labels.done' },
  { value: 'cantDo',  label: 'work_order_states_labels.cantDo' },
  { value: 'wontDo',  label: 'work_order_states_labels.wontDo' },
  { value: 'error',   label: 'work_order_states_labels.error' },
  { value: 'overdue', label: 'work_order_states_labels.overdue' },
];

/** States selectable in the UI. Other WORK_ORDER_STATES remain part of the model */
export const SELECTABLE_WORK_ORDER_STATES = WORK_ORDER_STATES.filter(
  s => s.value === 'open' || s.value === 'wip' || s.value === 'done',
);

export const WORK_ORDER_PRIORITIES: { value: WorkOrderPriority; label: string }[] = [
  { value:  1, label: 'Hoch' },
  { value:  0, label: 'Mittel' },
  { value: -1, label: 'Niedrig' },
];

export const STEP_STATES: { value: StepState; label: string }[] = [
  { value: 'open', label: 'open' },
  { value: 'done', label: 'work_order_states_labels.done' },
];

export const STATE_COLORS: Record<WorkOrderState, { bg: string; color: string }> = {
  open:          { bg: '#e3f2fd', color: '#1565c0' },
  orderAccepted: { bg: '#e0f7fa', color: '#00695c' },
  wip:     { bg: '#fff3e0', color: '#e65100' },
  done:    { bg: '#e8f5e9', color: '#2e7d32' },
  cantDo:  { bg: '#fce4ec', color: '#880e4f' },
  wontDo:  { bg: '#f3e5f5', color: '#6a1b9a' },
  error:   { bg: '#ffebee', color: '#b71c1c' },
  overdue: { bg: '#ffcdd2', color: '#c62828' },
};

/**

 * Firestore: `WorkOrder/<workOrderId>/Image/<uuid>`
 * Storage:   `workOrder/<workOrderId>/images/<uuid>.jpg` (full)
 *            `workOrder/<workOrderId>/images/preview_<uuid>.jpg` (preview)
 */
export interface WorkOrderImage {
  docId?: string;
  identifier: string;
  parentId?: string;
  contextId?: string;
  thumbnailData?: any;
  creationDate?: Date | Timestamp;
  timeStamp?: Date | Timestamp;
  updateTimestamp?: Date | Timestamp;
  createdByUserId?: string;
  createdByUserName?: string;
}

export const PRIORITY_COLORS: Record<WorkOrderPriority, { bg: string; color: string }> = {
  1:  { bg: '#ffcdd2', color: '#b71c1c' },
  0:  { bg: '#fff9c4', color: '#f57f17' },
  [-1]: { bg: '#e8f5e9', color: '#2e7d32' },
};

export const PRIORITY_ARROWS: Record<WorkOrderPriority, string> = {
  1: '▲',
  0: '■',
  [-1]: '▼',
};

export type CoverageLevel = 'low' | 'medium' | 'high';

export const COVERAGE_COLORS: Record<CoverageLevel, { bg: string; color: string }> = {
  low:    { bg: '#ffcdd2', color: '#b71c1c' },
  medium: { bg: '#fff9c4', color: '#f57f17' },
  high:   { bg: '#e8f5e9', color: '#2e7d32' },
};

export function coverageLevel(percentage: number): CoverageLevel {
  if (percentage <= 50) return 'low';
  if (percentage <= 75) return 'medium';
  return 'high';
}

export function formatCoverage(percentage: number): string {
  return `${Math.round(percentage * 10) / 10} %`;
}

/** Selectable work-quality values; labels are translation keys. */
export const WORK_QUALITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'good',   label: 'COCKPIT.WORK_ORDER.work_quality_good' },
  { value: 'medium', label: 'COCKPIT.WORK_ORDER.work_quality_medium' },
  { value: 'poor',   label: 'COCKPIT.WORK_ORDER.work_quality_poor' },
];

const WORK_QUALITY_LEVELS: Record<string, CoverageLevel> = { good: 'high', medium: 'medium', poor: 'low' };

export function deriveWorkQuality(coverage?: number | string | null): string | null {
  if (coverage == null || coverage === '' || !Number.isFinite(Number(coverage))) return null;
  const level = coverageLevel(Number(coverage));
  return Object.keys(WORK_QUALITY_LEVELS).find(key => WORK_QUALITY_LEVELS[key] === level) ?? null;
}

export function workQualityStyle(value?: string | null): { bg: string; color: string } {
  const level = value ? WORK_QUALITY_LEVELS[value] : undefined;
  return level ? COVERAGE_COLORS[level] : { bg: '#ffffff', color: '#333333' };
}

export const SEARCH_ORDER_MAIL_TEMPLATE = 'information-search-order';

export interface SearchOrderMailWorkOrderData {
  identifier?: string;
  title?: string;
  note?: string;
  dueDate?: string | null;
  plannedExecutionDate?: string | null;
  priority?: string;
}

export interface SearchOrderMailGeoObjectData {
  identifier: string;
  name: string;
}

export interface SearchOrderMailData {
  workOrders: SearchOrderMailWorkOrderData[];
  linkedGeoObjects: SearchOrderMailGeoObjectData[];
}

export interface MailDoc {
  contextId: string;
  to?: string[];
  bcc: string[];
  message: {
    attachments: any[];
  };
  template: {
    name: string;
    data: SearchOrderMailData;
  };
}